import {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import { isNativeCapacitorApp } from "@/integrations/clients/carAuction/nativeAuth";
import {
  isNativeTabletViewport,
  useIsNativeTablet,
} from "@/hooks/useIsNativeTablet";
import { useNativeCapacitorSetup } from "@/hooks/useNativeCapacitorSetup";

export type NativePlatformContextValue = {
  /** Running inside the Capacitor native shell (not mobile web). */
  isNative: boolean;
  /** Native tablet viewport (short edge ≥ 600px). False on web. */
  isNativeTablet: boolean;
};

const NativePlatformContext = createContext<NativePlatformContextValue | null>(
  null,
);

function buildFallbackValue(): NativePlatformContextValue {
  const isNative = isNativeCapacitorApp();
  return {
    isNative,
    isNativeTablet: isNative ? isNativeTabletViewport() : false,
  };
}

export function NativePlatformProvider({ children }: { children: ReactNode }) {
  useNativeCapacitorSetup();
  const isNativeTablet = useIsNativeTablet();

  const value = useMemo<NativePlatformContextValue>(
    () => ({
      isNative: isNativeCapacitorApp(),
      isNativeTablet,
    }),
    [isNativeTablet],
  );

  return (
    <NativePlatformContext.Provider value={value}>
      {children}
    </NativePlatformContext.Provider>
  );
}

/**
 * Prefer this over `isNativeCapacitorApp()` in React components.
 * Falls back to a direct Capacitor check when used outside the provider.
 */
export function useNativePlatform(): NativePlatformContextValue {
  const ctx = useContext(NativePlatformContext);
  return ctx ?? buildFallbackValue();
}
